import { CategorizedError } from "../types/errors";
import { categorizeError } from "../utils/errorMapper";

// 1. Grab the Backend URL from the environment (injected by CI or Vite)
// Accept either `VITE_API_BASE_URL` (preferred) or `VITE_API_URL` for
// compatibility with existing deployments.
const _ENV = (import.meta as any)?.env as Record<string, string> | undefined;
const ENV_API_URL = (_ENV?.VITE_API_BASE_URL ?? _ENV?.VITE_API_URL) as
  | string
  | undefined;

const normalize = (value?: string) =>
  value && value.trim().length ? value.replace(/\/$/, "") : undefined;

// 2. Determine the base URL
//    - If provided via env (production), use it
//    - If running locally (hostname === 'localhost') use local dev server
//    - Otherwise leave empty so we can use a relative `/api` path
const resolveBaseUrl = () => {
  const envUrl = normalize(ENV_API_URL);
  if (envUrl) return envUrl;
  if (typeof window === "undefined") return undefined;
  if (window.location.hostname === "localhost") return "http://localhost:5000";
  return undefined;
};

const BASE_URL = resolveBaseUrl();

export const PUBLIC_API_BASE = BASE_URL ? `${BASE_URL}/api` : "/api";

export async function publicJsonRequest<T = any>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  if (typeof window !== "undefined" && BASE_URL.includes("localhost")) {
    // Friendly dev-time warning so missing env settings are obvious
    // eslint-disable-next-line no-console
    console.warn(`publicJsonRequest: using local API base ${BASE_URL}`);
  }

  let response: Response;
  try {
    response = await fetch(`${PUBLIC_API_BASE}${path}`, {
      headers: {
        Accept: "application/json",
        ...(options?.headers as Record<string, string>),
      },
      ...options,
    });
  } catch (fetchError: any) {
    throw categorizeError(fetchError, "server-error");
  }

  const text = await response.text();
  if (!response.ok) {
    const snippet = text ? text.slice(0, 200) : "";
    let errorType:
      | "auth-required"
      | "forbidden"
      | "not-found"
      | "server-error" = "server-error";
    if (response.status === 401) errorType = "auth-required";
    else if (response.status === 403) errorType = "forbidden";
    else if (response.status === 404) errorType = "not-found";
    else if (response.status >= 500) errorType = "server-error";

    throw new CategorizedError(
      `API error ${response.status}${snippet ? `: ${snippet}` : ""}`,
      errorType,
      response.status,
    );
  }
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error("Public API returned invalid JSON");
  }
}

export async function fetchDiscordMemberCount(): Promise<number> {
  try {
    const data = await publicJsonRequest<{ count: number }>("/discord/members");
    return data?.count || 26;
  } catch (err) {
    console.error("Failed to fetch Discord member count", err);
    return 26; // fallback
  }
}
